import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenderRequirementsApi, CreateRequirementDto, UpdateRequirementDto } from '../../../api/tenderRequirements';
import { notifications } from '@mantine/notifications';

// Hook to get tender document requirements
export function useTenderRequirements(tenderId: string) {
  return useQuery({
    queryKey: ['tender-requirements', tenderId],
    queryFn: () => tenderRequirementsApi.listRequirements(tenderId),
    enabled: !!tenderId,
  });
}

// Hook to create a tender document requirement
export function useCreateTenderRequirement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: CreateRequirementDto }) =>
      tenderRequirementsApi.createRequirement(tenderId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['tender-requirements', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Document requirement created successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error creating tender requirement:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create document requirement. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to update a tender document requirement
export function useUpdateTenderRequirement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, requirementId, data }: { tenderId: string; requirementId: string; data: UpdateRequirementDto }) =>
      tenderRequirementsApi.updateRequirement(tenderId, requirementId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['tender-requirements', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Document requirement updated successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error updating tender requirement:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update document requirement. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to remove a tender document requirement
export function useRemoveTenderRequirement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, requirementId }: { tenderId: string; requirementId: string }) =>
      tenderRequirementsApi.removeRequirement(tenderId, requirementId),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['tender-requirements', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Document requirement removed successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error removing tender requirement:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to remove document requirement. Please try again.',
        color: 'red',
      });
    },
  });
}
